import Vue from "vue";
import {defineStore} from 'pinia'
import EventService from '@/services/APIService.js'


// Store for all defined search stores (for cleanup on logout)
const definedStores = new Map();

function makeSeparatedStore(defineStore) {
    return function(storeKey) {
        if (!definedStores.has(storeKey)) {
            definedStores.set(storeKey, defineStore(storeKey));
        }

        // @ts-expect-error
        return definedStores.get(storeKey)();
    };
}

/**
 * Reset all search store instances (call on logout)
 * This clears all cached store instances so they're recreated fresh on next login
 */
export function resetAllSearchStores() {
    definedStores.forEach((storeDefinition, key) => {
        try {
            // Get the store instance and reset it
            const store = storeDefinition();
            if (store && typeof store.$reset === 'function') {
                store.$reset();
            }
        } catch (e) {
            // Store may not be initialized yet, ignore
        }
    });
    // Clear the map so stores are recreated on next use
    definedStores.clear();
}

export const createSearchStore = makeSeparatedStore((key = 'default') =>
    defineStore(`search/${key}`,

        {
            state: () => {
                return {

                    state: 'initializing',
                    is_initialized: false,
                    mode: 'search',

                    object_type: null,
                    table_alias: null,
                    locale: null,
                    auto_refresh: false,


                    route: null,
                    route_id: null,

                    features: {},

                    fields: false,
                    loading_fields: false,

                    facets: [],
                    loading_facets: false,
                    selected_facets: [],

                    results: [],
                    loading_results: true,
                    loading_result: false,

                    autocomplete: [],

                    pagination: {},

                    column_settings_opened: false,
                    debug_view_opened: false,
                    results_max: 0,

                    bulk_actions: [],

                    query: {

                        search_input: null,
                        search_input_field: null

                    },

                    search_processing_time: 0,
                    view: 'default',
                    is_sorting: false,


                    selected: [],

                    search_process: null,
                    disabled: false,

                    autocomplete_controller: new AbortController(),
                    search_controller: new AbortController(),
                    facet_controller: new AbortController(),
                    prefetch_controller: new AbortController(),

                    // Result caching
                    results_cache: new Map(),
                    cache_max_size: 5,
                    cache_ttl: 30000, // 30 seconds
                    prefetched_page: null,
                    is_prefetching: false,

                    forced_expand: [],
                    forced_locale: null,
                    forced_query: [],
                    allowed_fields: [],
                    editable_fields: [],
                    preactive_fields: [],
                    bulk_actions_active: false


                }
            },
            getters: {

                getId(state){


                    let id = 'search::'+state.object_type;

                    if(state.table_alias){

                        id = id+'::'+state.table_alias;

                    }

                    return id;

                },


                getCurrentPage(state){

                    if(typeof state.pagination.page === 'undefined'){return 1;}
                    return parseInt(state.pagination.page);


                },

                getMaxPages(state){

                    let max = Math.ceil(state.results_max/state.pagination.itemsPerPage);

                    if(max <= 0){return 1;}
                    return parseInt(max);

                },

                getAvailableFields(state){

                    if (state.fields === false) {
                        return [];
                    }

                    let fields = state.fields;

                    if(state.allowed_fields.length > 0){


                        fields = fields.filter(fld => state.allowed_fields.indexOf(fld.field_id) !== -1);

                    }


                    return fields;


                },

                getSearchableFields(state){

                    if(!state.fields){return [];}
                    return state.fields.filter(fld => fld.is_searchable === true && fld.is_facet === false);

                },

                getActiveFields(state) {

                    if (state.fields === false) {
                        return [];
                    }

                    let fields = state.fields;

                    if(state.allowed_fields.length > 0){


                        fields = fields.filter(fld => state.allowed_fields.indexOf(fld.field_id) !== -1);

                    }



                    return fields.filter(fld => fld.is_active === true);

                },

                getFacetFields(state){

                    if (state.fields === false) {return [];}
                    let fields = state.fields.filter(fld => fld.is_facet === true && fld.disable_facet !== true);


                    if(state.forced_query){


                        fields = fields.filter(fld => state.forced_query[fld.field_id] === undefined);

                    }

                    return fields;

                },

                getActiveFacets(state) {

                    if (state.fields === false) {return [];}

                    // Forced fields are dropped here for the same reason getFacetFields drops
                    // them: the caller owns that field, so offering it as a filter invites the
                    // user to contradict something they cannot see. Without this a field that
                    // was already switched on keeps rendering after it is forced, showing values
                    // the current view has ruled out.
                    return state.fields.filter(fld =>
                        fld.is_facet === true
                        && fld.is_active_facet === true
                        && (!state.forced_query || state.forced_query[fld.field_id] === undefined)
                    );

                },

                getParams(state){


                    let expand = [];

                    let fields = state.getActiveFields;
                    for (let i = 0; i < fields.length; i++) {
                        expand = expand.concat(fields[i].requires_expand);
                    }

                    let facets = [];
                    let facet_fields = state.getActiveFacets;
                    for(let j = 0; j < facet_fields.length; j++){
                        facets = facets.concat(facet_fields[j].field_id);
                    }

                    let orderby = {};


                    if(state.pagination.sortBy){
                        for(let k =0; k < state.pagination.sortBy.length;k++){

                            let value = state.pagination.sortBy[k];
                            orderby[[value]] = (state.pagination.sortDesc[k]) ? 'DESC' : 'ASC';

                        }
                    }

                    expand.push('default');
                    expand.push('_actions');

                    if(state.forced_expand.length > 0){
                        expand = expand.concat(state.forced_expand);
                    }

                    expand = [...new Set(expand)];
                    facets = [...new Set(facets)];

                    let params = {

                        locale: state.forced_locale ?? state.locale,
                        query: state.query,
                        expand: expand,
                        facets: facets,
                        orderby: orderby,
                        page: this.pagination.page,
                        page_size: this.pagination.itemsPerPage

                    };


                    return params;

                },

                getParamsWithForcedQuery(state){


                    let self = this;

                    // getParams is a cached getter and getParams.query is a live reference to
                    // state.query. Build a brand-new params object with a cloned query so merging
                    // the forced query never mutates the cached getter result or the store query —
                    // otherwise forced keys (e.g. advanced_filter) stick around and never clear
                    // when forced_query empties.
                    let base = self.getParams;
                    let query = { ...base.query };

                    if (self.forced_query && Object.keys(self.forced_query).length !== 0) {

                        for (let key in self.forced_query) {
                            if (self.forced_query.hasOwnProperty(key)) {
                                query[key] = self.forced_query[key];
                            }
                        }
                    }


                    return { ...base, query };


                },

            },

            actions: {

                setMode(mode){

                    this.mode = mode;
                    return this;

                },

                toggleSorting(){

                    this.is_sorting = !this.is_sorting;
                    this.clearCache(); // Invalidate cache on sort toggle
                    this.loadResults();

                },

                setLocale(locale){

                    let self = this;

                    if(locale !== self.locale){
                        self.locale = locale;
                        self.clearCache(); // Invalidate cache on locale change
                        self.refresh();
                    }

                },

                clickResult(item){

                    let self = this;

                    self.last_clicked = item.id;
                    self.saveSettingsLocal();


                },

                clearResults(){

                    let self = this;
                    self.results = [];


                },

                toggleView(){

                    let self = this;


                    if(self.features.views){

                        let index = self.features.views.indexOf(self.view);
                        index = index+1;

                        self.view = self.features.views[index];

                        if(self.features.views[index] === undefined){

                            self.view = self.features.views[0];

                        }

                        self.saveSettingsLocal();
                        self.saveSettingsRemote();

                    }

                },

                reset(){

                    let self = this;

                    localStorage.removeItem(self.getId+'::'+'pagination');
                    sessionStorage.removeItem(self.getId+'::'+'query');

                    self.$reset();

                },

                init(){


                    let self = this;

                    return self.initialize().then(function(){

                        switch(self.mode){

                            case 'autocomplete':

                                self.state = 'ready';
                                // Nothing is in flight until the user types: an autocomplete
                                // runs no initial search, so the flag it was born with would
                                // otherwise sit on the combobox spinner until the first hit.
                                self.loading_results = false;
                                self.results = [];

                                break;


                            default:

                                self.loadResults('init');

                        }

                    });

                },

                async initialize() {

                    let self = this;
                    let settings = await self.loadSettings();

                    self.results = [];
                    self.features = settings.features;

                    if(self.features.views && self.view === 'default'){
                        self.view = self.features.views[0];
                    }

                    if(self.features.max_page_size <= self.pagination.itemsPerPage){
                       // self.pagination.itemsPerPage = self.features.page_size;
                    }

                    let fields = settings.fields;

                    // Preactivate fields which were provided by application

                    if(self.preactive_fields.length > 0){

                        for(let i = 0; i<self.preactive_fields.length;i++){

                            let index = fields.findIndex(fld => fld.field_id === self.preactive_fields[i]);

                            if(index !== -1){

                                fields[index].is_active = true;

                            }

                        }

                    }

                    // Activate/Deactivate fields depending on settings

                    if(settings.fields){

                        for(let i = 0; i<settings.fields.length; i++){

                            let index = fields.findIndex(fld => fld.field_id === settings.fields[i].field_id);

                            if(index > -1){

                                if(fields[i]){

                                    fields[index].is_active = settings.fields[i].is_active;

                                    if(self.forced_query[fields[i].field_id] === undefined){

                                        fields[index].is_active_facet = settings.fields[i].is_active_facet;

                                        // Comment in if sorting becomes available
                                        // fields[index].menu_order = settings.fields[i].menu_order;

                                    }

                                }

                            }

                        }

                    }

                    self.fields = fields.filter(itm => itm.is_visible !== false);
                    self.state = 'ready';

                    self.saveSettingsLocal();
                    self.is_initialized = true;


                },

                refresh() {

                    let self = this;
                    self.loadResults('refresh');

                },

                addAllowedField(field){

                    let self = this;

                    if(self.allowed_fields.indexOf(field) === -1){
                        self.allowed_fields.push(field);
                    }

                },

                addForcedExpand(expand){

                    let self = this;

                    if(self.forced_expand.indexOf(expand) === -1){
                        self.forced_expand.push(expand);
                    }

                },

                addEditableField(field){

                    if(this.editable_fields.indexOf(field) === -1){

                        this.editable_fields.push(field);

                    }

                    return this;

                },

                addPreactiveField(field){

                    if(this.preactive_fields.indexOf(field) === -1){

                        this.preactive_fields.push(field);
                    }

                    return this;

                },

                updateQuery(field, value, search_type = null){


                    let self = this;

                    // Assign a fresh object so Vue 2 / Pinia detects newly added
                    // facet keys (mutating the same reference is not reactive).
                    self.query = {
                        ...self.query,
                        [field]: value,
                        [field + '_search_method']: search_type,
                    };

                    self.clearCache(); // Invalidate cache on filter change
                    self.saveSettingsRemote();
                    return self.loadResults('updateQuery');


                },

                removeQueryField(field){

                    let self = this;
                    let query = { ...self.query };

                    let reload = true;


                    if(query[field] == '' || query[field] == null || (Array.isArray(query[field]) && query[field].length === 0)){reload = false;}


                    delete query[field];
                    delete query[field+'_search_method'];

                    // Assign a fresh object so the removal is reactive.
                    self.query = query;
                    self.clearCache(); // Invalidate cache on filter change

                    if(reload){
                        self.loadResults('removeQueryField');
                    }

                    self.saveSettingsRemote();

                },

                setPagesize(size){


                    let self = this;

                    self.pagination.itemsPerPage = size;
                    self.clearCache(); // Invalidate cache on page size change
                    self.refresh();
                    self.saveSettingsRemote();

                },

                resetFilters(){

                    let self = this;

                    self.query = {

                        search_input: null,
                        search_input_field: null,

                    }

                    self.pagination.page = 1;
                    self.clearCache(); // Invalidate cache on filter reset
                    self.loadResults('resetFilters');

                },



                patchResult(result) {
                    // Find the index of the item in the results array
                    const index = this.results.findIndex(r => r.id === result.id);

                    if (index !== -1) {
                        // Replace the existing item with the updated one
                        this.results.splice(index, 1, {
                            ...this.results[index], // Preserve existing properties
                            ...result // Overwrite with new properties
                        });
                    }
                },

                loadResult(id){

                    let self = this;
                    let result_index = self.results.findIndex(rst => rst.id === id);

                    self.loading_result = id;

                    return EventService.post('/api/v3/search/reload', {
                        id: id,
                        object: self.object_type,
                        params: self.getParams
                    },{
                        signal: this.search_controller.signal,
                    }).then((response) => {

                        Vue.set(self.results,result_index,response.data.payload);
                        self.saveSettingsLocal();
                        self.loading_result = false;

                    }).catch((error) => {
                        // Ignore canceled requests
                        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                            self.loading_result = false;
                            return;
                        }
                        throw error;
                    });

                },

                // Abort iff there is something to abort. The test used to be inverted, which
                // made this a no-op exactly while a search was in flight - so loadResults'
                // `state !== 'ready'` bail then dropped the newer search instead of superseding
                // the older one, and the results kept answering the query the user had already
                // moved on from. Cancelled requests are swallowed in loadResults' catch, which
                // has always described them as "intentional when a new search starts".
                abortSearch(){

                    let self = this;
                    if(self.state !== 'searching'){return;}
                    self.search_controller.abort();
                    self.state = 'ready';
                    self.loading_results = false;

                },

                loadResults(caller = null, scope = null, state = 'searching')  {

                    let self = this;
                    self.abortSearch();
                    if((self.state !== 'ready') || self.disabled === true ){return;}

                    // Check cache first (for page navigation calls)
                    let isPaginationCall = caller === 'gotoPage' || caller === 'SearchResultsTable@updateOptions';
                    if (isPaginationCall) {
                        let cached = self.getCachedResults(self.pagination.page);
                        if (cached) {
                            self.results = cached.results;
                            self.results_max = cached.results_max;
                            self.bulk_actions = cached.bulk_actions;
                            self.saveSettingsLocal();
                            self.prefetchNextPage();
                            return Promise.resolve();
                        }
                    }

                    self.loading_results = true;
                    self.state = state;
                    self.selected = [];

                    if(scope === null){scope = ['results'];}
                    if(self.is_sorting){scope.push('sorting');}

                    // Use dedicated autocomplete_controller for autocomplete mode
                    let controller;
                    if(caller === 'search_combobox' || self.mode === 'autocomplete') {
                        this.autocomplete_controller.abort();
                        this.autocomplete_controller = new AbortController();
                        controller = this.autocomplete_controller;
                    } else {
                        // Abort the search this one supersedes, the way the autocomplete branch
                        // above already does.
                        //
                        // abortSearch() at the top of this method is meant to cover it, but only
                        // fires while the state still reads 'searching' — and initialize() sets
                        // the state back to 'ready' before its own loadResults(), so a search
                        // started through init() never cancels the one already running. Both
                        // then run to completion and the one that answers last wins rather than
                        // the one that was asked last. The superseded query is usually the
                        // broader, slower one, which makes it the likelier of the two to land
                        // second and overwrite the right answer with a stale one.
                        this.search_controller.abort();
                        this.search_controller = new AbortController();
                        controller = this.search_controller;
                    }

                    let payload = {
                        object: self.object_type,
                        scope: scope,
                        params: JSON.parse(JSON.stringify(self.getParamsWithForcedQuery))
                    };

                    return EventService.post('/api/v3/search/'+self.object_type, payload,{
                        signal: controller.signal,
                    }).then((response) => {

                        if(response){

                            self.handleResults(response.data);
                            self.saveSettingsLocal();

                            // Cache current results and prefetch next page
                            if (caller !== 'search_combobox' && self.mode !== 'autocomplete') {
                                self.cacheResults(
                                    self.pagination.page,
                                    self.results,
                                    self.results_max,
                                    self.bulk_actions
                                );
                                self.prefetchNextPage();
                            }

                            if(self.getMaxPages < self.getParams.page && self.getParams.page > 1){
                                self.gotoPage(1);
                            }

                        }

                    }).catch((error) => {
                        // Cancelled requests are intentional when a new search starts. The search
                        // that superseded this one has already raised these flags for its own run,
                        // so clearing them here would blank a spinner that belongs to a request
                        // still in flight.
                        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                            return;
                        }

                        // Any other failure ends this search with no results. handleResults() is
                        // what normally lowers these and it never runs, so leaving them raised
                        // keeps the spinner going for a search that is already over - and unlike
                        // the cancelled case nothing follows to clear it.
                        self.loading_results = false;
                        self.state = 'ready';

                        throw error;
                    });

                },

                loadFacets(facet = null){

                    let self = this;

                    // With the forced query, not without it: a facet describes what is available
                    // inside what the caller has already narrowed to. Counting across everything
                    // offers filters that lead nowhere — a shop with no orders in the current
                    // view, listed with the total it has everywhere else.
                    //
                    // The forced field itself is unaffected: the server drops a facet's own
                    // filter before counting it, so it still reports every value it could take.
                    //
                    // Cloned rather than shared, which getParams is not — the facets line below
                    // writes into this object, and getParams is a cached getter.
                    let params = JSON.parse(JSON.stringify(self.getParamsWithForcedQuery));

                    if(facet !== null){

                        params.facets = [facet];

                    }

                     this.facet_controller.abort();
                     this.facet_controller = new AbortController();

                    return EventService.post('/api/v3/search', {
                        object: self.object_type,
                        scope: ['facets'],
                        params: params
                    },{
                        signal: this.facet_controller.signal,
                    }).then((response) => {

                        response.data.payload.facets.forEach(function(val){

                            let index = self.facets.findIndex(fct => fct.field_id === val.field_id);
                            if(index !== -1){
                                self.facets.splice(index,1);
                            }

                            self.facets.push(val)

                        });


                    }).catch((error) => {
                        // Ignore canceled requests
                        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                            return;
                        }
                        throw error;
                    });


                },

                updateSort(payload){

                    let self = this;

                    self.state = 'loading';
                    payload.object = self.object_type;
                    payload.forced_query = self.forced_query;

                    return EventService.post('/api/v3/search/reorder',payload).then(function(){

                        self.state = 'ready';
                        self.loadResults('updateSort',['results'],'sorting')

                    });


                },

                handleResults(data){

                    let self = this;

                    self.loading_results = false;

                    data.payload.results.forEach((r, index) => {
                        data.payload.results[index] = self.addResultEditorFlags(r);
                    });

                    if(data.payload.results !== undefined){
                        self.results = data.payload.results;
                        self.bulk_actions =  data.payload.bulk_actions;
                    } else {
                        self.results = [];
                    }

                    self.results_max = data.payload.results_max;


                    self.search_processing_time = parseFloat(data.processing_time);

                    self.state = 'ready';

                },

                addResultEditorFlags(result){


                    result.pling_editing = false;
                    result.pling_editing2 = false;
                    result.pling_editing3 = false;
                    result.pling_editing4 = false;

                    return result;

                },

                setForcedLocale(locale){


                    let self = this;
                    self.forced_locale = locale;


                },

                async loadSettings(){

                    let self = this;

                    let response = await EventService.get('/api/v3/search/settings/'+self.object_type+'/'+self.getId);
                    let settings = {};

                    if(typeof response.data.payload === 'object'){

                        settings = response.data.payload;

                    }

                   /* let settings = localStorage.getItem(self.getId+'::'+'presets');
                    settings = JSON.parse(settings);

                    if(settings === null){settings = {};}*/


                    if(typeof settings.view !== undefined){
                        self.view = settings.view;
                    }


                    let pagination = localStorage.getItem(self.getId + '::' + 'pagination');
                    pagination = JSON.parse(pagination);


                    if(typeof settings.page_size === 'undefined'){
                        settings.page_size = 50;
                    }

                    if (pagination !== null) {

                        pagination.itemsPerPage = settings.page_size;
                        self.pagination = pagination;

                    }
                    else {
                        self.pagination = {};
                        self.pagination.itemsPerPage = settings.page_size;
                    }

                    let facets = sessionStorage.getItem(self.getId+'::'+'facets');

                    facets = JSON.parse(facets);
                    if(facets !== null){
                        settings.facets = facets;
                        self.facets = settings.facets;
                    }



                    let query = sessionStorage.getItem(self.getId+'::'+'query');
                    query = JSON.parse(query);
                    if(query !== null){
                        settings.query = query;
                        self.query = settings.query;
                    }

                    if(typeof settings.search_input_field !== undefined){

                        self.query.search_input_field = settings.search_input_field;

                    }

                    return settings;

                },

                saveSettingsRemote(){

                    let self = this;
                    let settings = {
                        search_input_field: self.query.search_input_field,
                        page_size: self.pagination.itemsPerPage,
                        view: self.view,
                        fields: self.getProcessedFields()
                    }

                    return EventService.put('/api/v3/search/settings/'+self.getId, settings).then((response) => {

                        console.log('Saved Settigs on Server');

                    });

                },

                saveSettingsLocal(){

                    let self = this;

                    self.savePagination();
                    self.saveQuery();
                    self.saveFacets();


                },

                getProcessedFields(){

                    let self = this;

                    let fields = self.fields;
                    let processed_fields = [];

                    fields.forEach(function (element) {

                        processed_fields.push({
                            field_id: element.field_id,
                            is_active: element.is_active,
                            menu_order: element.menu_order,
                            is_active_facet: element.is_active_facet,
                        });

                    });


                    return processed_fields;


                },


                savePagination(){

                    let self = this;
                    localStorage.setItem(self.getId+'::'+'pagination',JSON.stringify(self.pagination));

                },

                saveQuery(){

                    let self = this;
                    let saved_query = JSON.parse(JSON.stringify(self.getParams.query));

                    if(self.forced_query){

                        Object.keys(self.forced_query).forEach(key => {
                            if (saved_query.hasOwnProperty(key)) {
                                delete saved_query[key];
                            }
                        });

                    }

                    sessionStorage.setItem(self.getId+'::'+'query',JSON.stringify(saved_query));

                },

                saveFacets(){

                    let self = this;
                    sessionStorage.setItem(self.getId+'::'+'facets',JSON.stringify(self.facets));

                },

                gotoPage(page_num){

                    let self = this;

                    self.pagination.page = page_num;
                    self.loadResults('gotoPage',['results'])


                },

                // ==========================================
                // Result Caching Methods
                // ==========================================

                /**
                 * Generate cache key for current query state
                 */
                getCacheKey(page = null) {
                    let self = this;
                    let targetPage = page ?? self.pagination.page;
                    let queryHash = JSON.stringify(self.getParamsWithForcedQuery.query);
                    let sortHash = JSON.stringify(self.pagination.sortBy) + JSON.stringify(self.pagination.sortDesc);
                    return `${queryHash}::${sortHash}::${targetPage}`;
                },

                /**
                 * Get cached results for a specific page
                 */
                getCachedResults(page) {
                    let self = this;
                    let key = self.getCacheKey(page);
                    let cached = self.results_cache.get(key);

                    if (!cached) return null;

                    // Check TTL
                    if (Date.now() - cached.timestamp > self.cache_ttl) {
                        self.results_cache.delete(key);
                        return null;
                    }

                    return cached;
                },

                /**
                 * Store results in cache
                 */
                cacheResults(page, results, results_max, bulk_actions) {
                    let self = this;
                    let key = self.getCacheKey(page);

                    // Evict oldest entries if cache is full
                    if (self.results_cache.size >= self.cache_max_size) {
                        let oldestKey = self.results_cache.keys().next().value;
                        self.results_cache.delete(oldestKey);
                    }

                    self.results_cache.set(key, {
                        results: results,
                        results_max: results_max,
                        bulk_actions: bulk_actions,
                        timestamp: Date.now()
                    });
                },

                /**
                 * Clear all cached results (call on query/filter change)
                 */
                clearCache() {
                    let self = this;
                    self.results_cache.clear();
                    self.prefetched_page = null;
                    self.prefetch_controller.abort();
                },

                /**
                 * Prefetch next page in background
                 */
                prefetchNextPage() {
                    let self = this;
                    let nextPage = self.pagination.page + 1;

                    // Don't prefetch if already at last page
                    if (nextPage > self.getMaxPages) return;

                    // Don't prefetch if already cached
                    if (self.getCachedResults(nextPage)) return;

                    // Abort any running prefetch and start new one
                    self.prefetch_controller.abort();
                    self.prefetch_controller = new AbortController();
                    self.is_prefetching = true;

                    let params = JSON.parse(JSON.stringify(self.getParamsWithForcedQuery));
                    params.page = nextPage;

                    let payload = {
                        object: self.object_type,
                        scope: ['results'],
                        params: params
                    };

                    EventService.post('/api/v3/search/' + self.object_type, payload, {
                        signal: self.prefetch_controller.signal,
                    }).then((response) => {
                        if (response && response.data && response.data.payload) {
                            let results = response.data.payload.results.map(r => self.addResultEditorFlags(r));
                            self.cacheResults(
                                nextPage,
                                results,
                                response.data.payload.results_max,
                                response.data.payload.bulk_actions
                            );
                            self.prefetched_page = nextPage;
                        }
                        self.is_prefetching = false;
                    }).catch((error) => {
                        self.is_prefetching = false;
                        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                            return;
                        }
                    });
                },

            },

        }

    ),
);
